import Controller from '@ember/controller';
import {
  action,
  computed,
  get,
  getProperties,
  set,
  setProperties,
} from '@ember/object';
import { alias, equal, notEmpty } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';
import { Promise } from 'rsvp';

export default Controller.extend({
  contactsCache: service(),
  communicationsService: service(),
  fundsService: service(),
  router: service(),
  store: service(),

  queryParams: ['contactId', 'fromDetailPage', 'fromTaskId', 'disableGroups'],

  contactId: null,
  fromDetailPage: null,
  fromTaskId: null,
  disableGroups: null,
  error: null,

  record: alias('model.record'),
  changeset: alias('model.changeset'),
  reasons: alias('communicationsService.reasons'),
  mediums: alias('communicationsService.mediums'),

  forContact: equal('changeset.for', 'contact'),
  forGroup: equal('changeset.for', 'group'),
  hasGroups: notEmpty('contactsCache.sortedGroups'),
  groups: computed('contactsCache.groupsCacheIndex', function () {
    return this.contactsCache.sortedGroups;
  }),

  reset() {
    setProperties(this, {
      contactId: null,
      fromDetailPage: null,
      fromTaskId: null,
      disableGroups: null,
      error: null,
    });
  },

  deleteRoute: computed('model.record.isNew', function () {
    return undefined;
  }),
  deleteRouteParams: computed('model.record.id', function () {
    return undefined;
  }),
  showGroupsOption: computed(
    'record.isNew',
    'hasGroups',
    'disableGroups',
    function () {
      return this.record.isNew && this.hasGroups && isEmpty(this.disableGroups);
    },
  ),
  showGoBack: alias('model.saved'),
  showSaved: computed('model.saved', 'model.changeset.isPristine', function () {
    return (
      this.model.saved &&
      (this.forGroup || get(this, 'model.changeset.isPristine'))
    );
  }),
  showSaveFormActions: computed(
    'model.saved',
    'model.changeset.isDirty',
    function () {
      return (
        !this.model.saved ||
        (this.forContact && get(this, 'model.changeset.isDirty'))
      );
    },
  ),

  generateCommunicationsForGroup() {
    let changeset = this.changeset;
    let clonedProps = getProperties(
      changeset,
      'completedAt',
      'summary',
      'notes',
      'reason',
      'medium',
      'massCommunication',
    );
    let groupContacts = get(
      this,
      `contactsCache.groupContacts.${get(changeset, 'group')}.contacts`,
    );
    return groupContacts.map((contact) => {
      return this.store.createRecord(
        'communication',
        Object.assign(
          {
            contactId: contact.id,
          },
          clonedProps,
        ),
      );
    });
  },

  saveForGroup() {
    return new Promise((resolve, reject) => {
      this.generateCommunicationsForGroup()
        .reduce(function (promise, record) {
          return promise.then(function () {
            return record.save();
          });
        }, Promise.resolve())
        .then(
          () => {
            resolve();
          },
          (error) => {
            reject(error);
          },
        );
    });
  },

  save: task(function* () {
    let changeset = this.changeset;
    yield changeset.validate();
    if (get(changeset, 'isValid')) {
      try {
        yield this.fundsService.confirmValidFundToken();
        if (this.forContact) {
          yield changeset.save();
        } else {
          yield this.saveForGroup();
        }
        set(this, 'model.saved', true);
      } catch (error) {
        if (this.record.errors.length > 0) {
          this.record.errors.forEach(({ attribute, message }) => {
            changeset.pushErrors(attribute, message);
          });
        } else {
          set(this, 'error', error);
        }
      }
    }
  }).drop(),

  goBack: action(function () {
    if (this.model.returnToContactDetailsPage) {
      this.router.transitionTo(
        'funds.fund.contacts.contact',
        this.model.contact.id,
      );
    } else {
      window.history.back();
    }
  }),

  setFor: action(function (val) {
    setProperties(this.changeset, {
      for: val,
      contactId: null,
      group: null,
      massCommunication: val === 'group',
    });
  }),
});
